export type DoorCurrent = {
  period: string;
  timeStart: string;
  timeEnd: string;
  in: number;
  out: number;
  net: number;
  currentOccupancy: number;
  totalIn: number;
  totalOut: number;
  latestPeriodIn: number;
  latestPeriodOut: number;
  latestPeriodStart: string;
  latestPeriodEnd: string;
  latestPeriodAt: string;
  lastEventAt: string;
};

// `floorCurrent` is an additive backend contract. It repeats the authoritative
// floor aggregate on each door so a single REST or websocket door update can
// refresh the floor summary without a second endpoint.
export type FloorCurrent = DoorCurrent & {
  scope: "floor";
  calculation: "daily_net";
  baseline: number;
  baselineAt: string;
  sensorCount: number;
};

export type Door = {
  id: string;
  floorID: string;
  locationID: string;
  sensorID: string;
  deviceSN: string;
  alias: string;
  status: string;
  timestamp: string;
  recordType: "door";
  compoundKey: string;
  current: DoorCurrent | null;
  floorCurrent?: FloorCurrent | null;
};

export type DoorDataPayload = Omit<Door, "current"> & {
  eventID: string;
  reportType: string;
  eventTime: string;
  deltaIn: number;
  deltaOut: number;
  // Durable per-device order keys are decimal strings and can exceed
  // Number.MAX_SAFE_INTEGER.
  sequence: string;
  current: DoorCurrent;
};

export type DoorState = Door &
  Partial<
    Pick<
      DoorDataPayload,
      | "eventID"
      | "reportType"
      | "eventTime"
      | "deltaIn"
      | "deltaOut"
      | "sequence"
    >
  > & {
    // Bounded client-side retry history. SQS suppresses immediate duplicates;
    // this also protects against a device retry outside that deduplication window.
    acceptedEventIDs?: string[];
  };

export type DoorDataMessage = {
  action: "doorData";
  payload: DoorDataPayload;
};

function isString(field: unknown): field is string {
  return typeof field === "string";
}

function isNonEmptyString(field: unknown): field is string {
  return isString(field) && field.length > 0;
}

function isFiniteNumber(field: unknown): field is number {
  return typeof field === "number" && Number.isFinite(field);
}

export function isDoorCurrent(value: unknown): value is DoorCurrent {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const current = value as Partial<DoorCurrent>;
  return (
    isNonEmptyString(current.period) &&
    isNonEmptyString(current.timeStart) &&
    isNonEmptyString(current.timeEnd) &&
    isFiniteNumber(current.in) &&
    isFiniteNumber(current.out) &&
    isFiniteNumber(current.net) &&
    isFiniteNumber(current.currentOccupancy) &&
    isFiniteNumber(current.totalIn) &&
    isFiniteNumber(current.totalOut) &&
    isFiniteNumber(current.latestPeriodIn) &&
    isFiniteNumber(current.latestPeriodOut) &&
    isString(current.latestPeriodStart) &&
    isString(current.latestPeriodEnd) &&
    isString(current.latestPeriodAt) &&
    isString(current.lastEventAt)
  );
}

export function isFloorCurrent(value: unknown): value is FloorCurrent {
  if (!isDoorCurrent(value)) {
    return false;
  }

  const current = value as Partial<FloorCurrent>;
  return (
    current.scope === "floor" &&
    current.calculation === "daily_net" &&
    isFiniteNumber(current.baseline) &&
    isNonEmptyString(current.baselineAt) &&
    isFiniteNumber(current.sensorCount) &&
    Number.isInteger(current.sensorCount) &&
    current.sensorCount >= 0
  );
}

export function isDoorDataPayload(value: unknown): value is DoorDataPayload {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const payload = value as Partial<DoorDataPayload>;

  return (
    isNonEmptyString(payload.id) &&
    isNonEmptyString(payload.floorID) &&
    isNonEmptyString(payload.locationID) &&
    isNonEmptyString(payload.sensorID) &&
    isNonEmptyString(payload.deviceSN) &&
    isNonEmptyString(payload.alias) &&
    isNonEmptyString(payload.status) &&
    isNonEmptyString(payload.timestamp) &&
    payload.recordType === "door" &&
    isNonEmptyString(payload.compoundKey) &&
    isNonEmptyString(payload.eventID) &&
    isNonEmptyString(payload.reportType) &&
    isNonEmptyString(payload.eventTime) &&
    isNonEmptyString(payload.sequence) &&
    /^\d+$/.test(payload.sequence) &&
    isFiniteNumber(payload.deltaIn) &&
    isFiniteNumber(payload.deltaOut) &&
    isDoorCurrent(payload.current) &&
    (payload.floorCurrent === undefined ||
      payload.floorCurrent === null ||
      isFloorCurrent(payload.floorCurrent))
  );
}

// display-layer door consumed by the floor-plan UI: a DoorState joined with
// its sensor-derived status (Occupied/Unoccupied/No status), or a bare svg
// shape that has no BE record yet (id is empty until a sensor is assigned).
export type DoorDisplay = Partial<DoorState> & {
  floorID: string;
  sensorID: string;
  alias: string;
  status: string;
  timestamp?: string;
};
